import {
  type ApiErrorResponseData,
  type CustomErrorResponse,
  ErrorType,
  type ValidatorErrorResponse,
} from '~/common/types/api.type';
import { isApiError } from '~/common/utils/api.util';
import dialog from '~/common/utils/dialog.util';
import { APP_MODE, ViteAppMode } from '~/common/utils/env.util.ts';
import { resetAuthData } from '~/features/auth/utils/auth.util';

import type { AxiosError } from 'axios';
import type { ReactNode } from 'react';

const DEFAULT_FALLBACK_MESSAGE = '일시적인 오류가 발생했습니다. 새로고침 후 다시 시도해주세요.';

interface ErrorHandlerOptions {
  /** 에러 다이얼로그 제목 (어떤 작업 중 오류인지) */
  title: string;
  /** 서버 메시지가 없을 때 표시할 기본 메시지 */
  fallbackMessage?: string;
  /** 다이얼로그 표시 여부 (기본값: true) */
  showDialog?: boolean;
  /** 401 에러 시 로그인 페이지 리다이렉트 비활성화 (기본값: false) */
  disableAuthCheck?: boolean;
}

/**
 * API 에러 응답에서 content 메시지 추출
 */
function extractApiErrorContent(data: ApiErrorResponseData): ReactNode {
  if (data.type === ErrorType.CUSTOM) {
    return (data as CustomErrorResponse).message;
  }
  if (data.type === ErrorType.VALIDATOR) {
    const { errors } = data as ValidatorErrorResponse;
    return (
      <ul>
        {errors.map((e, i) => (
          <li key={i}>{e.message}</li>
        ))}
      </ul>
    );
  }
  return null;
}

/**
 * 네트워크 에러 판별
 */
function isNetworkError(error: AxiosError): boolean {
  return !error.response && error.code !== 'ECONNABORTED';
}

/**
 * 타임아웃 에러 판별
 */
function isTimeoutError(error: AxiosError): boolean {
  return error.code === 'ECONNABORTED';
}

/**
 * 401 Unauthorized 처리 (로그인 페이지로 리다이렉트)
 */
function handleUnauthorized(): void {
  resetAuthData();
  window.location.href = '/auth/signin';
}

/**
 * 전역 에러 핸들러
 * @description API 에러, 네트워크 에러, 로컬 에러를 일관되게 처리
 * @param error - 에러 객체
 * @param options - 핸들러 옵션
 * @param options.title - 에러 다이얼로그 제목 (필수)
 * @param options.fallbackMessage - 서버 메시지가 없을 때 표시할 기본 메시지
 * @param options.showDialog - 다이얼로그 표시 여부 (기본값: true)
 */
export function globalErrorHandler(error: unknown, options: ErrorHandlerOptions) {
  const {
    title,
    fallbackMessage = DEFAULT_FALLBACK_MESSAGE,
    showDialog = true,
    disableAuthCheck = false,
  } = options;

  let dialogTitle = title;
  let dialogContent: ReactNode = fallbackMessage;

  if (isApiError<ApiErrorResponseData>(error)) {
    const status = error.response?.status;

    // 401 Unauthorized - 로그인 페이지로 리다이렉트 (disableAuthCheck가 false일 때만)
    if (status === 401 && !disableAuthCheck) {
      handleUnauthorized();
      return;
    }

    // 네트워크 에러 - 자체 제목 사용
    if (isNetworkError(error)) {
      dialogTitle = '네트워크 오류';
      dialogContent = '네트워크 연결을 확인해주세요.';
    }
    // 타임아웃 에러 - 자체 제목 사용
    else if (isTimeoutError(error)) {
      dialogTitle = '요청 시간 초과';
      dialogContent = '요청 시간이 초과되었습니다. 다시 시도해주세요.';
    }
    // API 에러 - 호출부 title + 서버 메시지
    else if (error.response?.data) {
      const serverContent = extractApiErrorContent(error.response.data);
      dialogContent = serverContent ?? fallbackMessage;
    }

    // 개발 환경에서는 콘솔에 상세 정보 출력
    if (APP_MODE === ViteAppMode.DEVELOPMENT) {
      // eslint-disable-next-line no-console
      console.warn('[API Error]', error.response?.data);
    }
  } else if (APP_MODE === ViteAppMode.DEVELOPMENT) {
    // 로컬 에러 (비즈니스 로직 에러 등) - 호출부 title + fallback 메시지
    // eslint-disable-next-line no-console
    console.warn('[Local Error]', error);
  }

  if (showDialog) {
    dialog.message({ title: dialogTitle, content: dialogContent });
  }
}
